import apiClient from "./client";
import { unwrapApiResponse } from "../utils/apiError";

export const notificationAPI = {
  create: async ({ receiverId, receiverRole, typeNotification, title, description }) => {
    const response = await apiClient.post("/teacher/notifications", {
      receiverId,
      receiverRole,
      typeNotification,
      title,
      description,
    });
    return unwrapApiResponse(response);
  },

  createBulk: async ({ target, classId, typeNotification, title, description }) => {
    const payload = {
      target,
      typeNotification,
      title,
      description,
    };

    if (target === "class") {
      payload.classId = classId;
    }

    const response = await apiClient.post("/teacher/notifications/bulk", payload);
    return unwrapApiResponse(response);
  },

  getReceived: async () => {
    const response = await apiClient.get("/teacher/notifications/received");
    return unwrapApiResponse(response);
  },

  getSent: async () => {
    const response = await apiClient.get("/teacher/notifications/sent");
    return unwrapApiResponse(response);
  },

  markAsSeen: async (id) => {
    const response = await apiClient.patch(`/teacher/notifications/${id}/seen`);
    return unwrapApiResponse(response);
  },

  getById: async (id) => {
    const response = await apiClient.get(`/teacher/notifications/${id}`);
    return unwrapApiResponse(response);
  },

  delete: async (id) => {
    const response = await apiClient.delete(`/teacher/notifications/${id}`);
    return unwrapApiResponse(response);
  },

  update: async (id, data) => {
    const response = await apiClient.patch(`/teacher/notifications/${id}`, data);
    return unwrapApiResponse(response);
  },

  getReceivers: async () => {
    const response = await apiClient.get("/teacher/notifications/receivers");
    return unwrapApiResponse(response);
  },

  getThread: async (id) => {
    const response = await apiClient.get(`/teacher/notifications/${id}/thread`);
    return unwrapApiResponse(response);
  },

  reply: async (id, { title, description }) => {
    const payload = {
      description: description.trim(),
    };
    const trimmedTitle = title?.trim();

    if (trimmedTitle) {
      payload.title = trimmedTitle;
    }

    const response = await apiClient.post(`/teacher/notifications/${id}/reply`, payload);
    return unwrapApiResponse(response);
  },
};

export function isSystemNotification(notification) {
  if (notification?.sender) {
    return notification.sender.id == null && notification.sender.role == null;
  }

  return !notification?.senderId;
}

export function isEventNotification(notification) {
  return Boolean(notification?.eventType || notification?.resourceType);
}

export function canEditNotification(notification, currentUserId) {
  if (!notification || isSystemNotification(notification) || isEventNotification(notification)) {
    return false;
  }

  const senderId = notification?.sender?.id ?? notification?.senderId;
  return Boolean(currentUserId && senderId && senderId === currentUserId);
}

export function canReplyToNotification(notification, currentUserId) {
  if (!notification || isSystemNotification(notification)) {
    return false;
  }

  const targetType = notification.target?.type;
  const isOwnGroupSend =
    (targetType === "class" || targetType === "all_students") &&
    currentUserId &&
    notification.senderId === currentUserId;

  return !isOwnGroupSend;
}

export function normalizeNotificationThread(payload) {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (!payload || typeof payload !== "object") {
    return [];
  }

  if (Array.isArray(payload.thread)) {
    return payload.thread;
  }

  if (Array.isArray(payload.messages)) {
    return payload.messages;
  }

  if (Array.isArray(payload.items)) {
    return payload.items;
  }

  if (Array.isArray(payload.notifications)) {
    return payload.notifications;
  }

  if (Array.isArray(payload.data)) {
    return payload.data;
  }

  return [];
}

export function sortNotificationThread(items = []) {
  return [...items].sort((a, b) => {
    const first = new Date(a?.createdAt || 0).getTime();
    const second = new Date(b?.createdAt || 0).getTime();

    return first - second;
  });
}
